<template>
  <div class="signup-page-container">
    <div class="container py-5">
      <div id="get-sign-in-code"></div>
      
      <div class="signup-card shadow-premium" ref="formContainer">
        <!-- Card Header Banner -->
        <div class="signup-header">
          <div class="header-overlay"></div>
          <div class="header-content text-center">
            <router-link :to="{ name: 'Home' }" class="brand-logo-link">
              <img
                class="brand-logo img-fluid animate-logo"
                alt="SECC Logo"
                src="../../../assets/secclogo.png"
              />
            </router-link>
            <h3 class="system-title font-moul">{{ $t("message.system_title") }}</h3>
            <h4 class="form-action-title">
              {{ $t("message.create_account") }} {{ $t("message.or") }} CamDigiKey
            </h4>
            
            <div class="sso-actions mt-3">
              <button
                type="button"
                class="btn-camdigikey-sso"
                @click.prevent="getCamDigikey"
              >
                <img src="@/assets/images/unnamed.webp" class="btn-cam-img" alt="" />
                <span>{{ $t("message.signin_camdigikey") }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Card Body -->
        <div class="signup-body">
          <!-- Form 1: Registration Form -->
          <div v-if="showNumberInput" class="form-section animate-fade-in">
            <h5 class="section-title-custom font-moul text-center mb-4">
              {{ $t("message.create_account") }}
            </h5>
            
            <form @submit.prevent>
              <div class="row g-4">
                <!-- Participant Type -->
                <div class="col-md-6 col-12">
                  <div class="form-group-custom">
                    <label class="form-label-custom">
                      {{ $t("message.type_of_trainee") }} <span class="text-danger">*</span>
                    </label>
                    <select
                      class="form-select-custom"
                      :class="{ 'is-invalid': v$.form.participant_type.$error }"
                      v-model="form.participant_type"
                      @change="updateForm"
                    >
                      <option value="" disabled selected>Choose type...</option>
                      <option value="1">{{ $t("message.individual") }}</option>
                      <option value="2">{{ $t("message.institution") }}</option>
                    </select>
                  </div>
                </div>

                <!-- Company Name (v-if participant_type == 2) -->
                <div class="col-md-6 col-12" v-if="form.participant_type == 2">
                  <div class="form-group-custom animate-fade-in">
                    <label class="form-label-custom">
                      {{ $t("message.company_name") }} <span class="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      v-model.trim="form.company_name"
                      @input="updateForm"
                      class="form-control-custom"
                      :placeholder="$t('message.company_name')"
                      :class="{ 'is-invalid': v$.form.company_name.$error }"
                    />
                  </div>
                </div>

                <!-- Khmer Full Name -->
                <div class="col-12">
                  <label class="form-label-custom mb-2">
                    {{ $t("message.full_name") }} ({{ $t("message.in_khmer") }}) <span class="text-danger">*</span>
                  </label>
                  <div class="row g-3">
                    <div class="col-md-6 col-12">
                      <input
                        type="text"
                        v-model.trim="form.lastname"
                        @input="updateForm"
                        class="form-control-custom"
                        :placeholder="$t('message.last_name')"
                        :class="{ 'is-invalid': v$.form.lastname.$error || errors?.lastname }"
                      />
                      <div class="invalid-feedback-custom" v-if="errors?.lastname">
                        {{ errors?.lastname[0] }}
                      </div>
                    </div>
                    <div class="col-md-6 col-12">
                      <input
                        type="text"
                        v-model.trim="form.firstname"
                        @input="updateForm"
                        class="form-control-custom"
                        :placeholder="$t('message.first_name')"
                        :class="{ 'is-invalid': v$.form.firstname.$error || errors?.firstname }"
                      />
                      <div class="invalid-feedback-custom" v-if="errors?.firstname">
                        {{ errors?.firstname[0] }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Latin Full Name -->
                <div class="col-12">
                  <label class="form-label-custom mb-2">
                    {{ $t("message.full_name") }} ({{ $t("message.in_latin") }}) <span class="text-danger">*</span>
                  </label>
                  <div class="row g-3">
                    <div class="col-md-6 col-12">
                      <input
                        type="text"
                        v-model="form.lastname_en"
                        @input="updateForm"
                        class="form-control-custom"
                        :placeholder="$t('message.last_name')"
                        :class="{ 'is-invalid': v$.form.lastname_en.$error || errors?.lastname_en }"
                      />
                      <div class="invalid-feedback-custom" v-if="errors?.lastname_en">
                        {{ errors?.lastname_en[0] }}
                      </div>
                    </div>
                    <div class="col-md-6 col-12">
                      <input
                        type="text"
                        v-model="form.firstname_en"
                        @input="updateForm"
                        class="form-control-custom"
                        :placeholder="$t('message.first_name')"
                        :class="{ 'is-invalid': v$.form.firstname_en.$error || errors?.firstname_en }"
                      />
                      <div class="invalid-feedback-custom" v-if="errors?.firstname_en">
                        {{ errors?.firstname_en[0] }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Gender -->
                <div class="col-md-6 col-12">
                  <div class="form-group-custom">
                    <label class="form-label-custom">
                      {{ $t("message.gender") }} <span class="text-danger">*</span>
                    </label>
                    <select
                      class="form-select-custom"
                      :class="{ 'is-invalid': v$.form.gender.$error }"
                      v-model="form.gender"
                      @change="updateForm"
                    >
                      <option value="" disabled selected>Choose gender...</option>
                      <option value="1">ប្រុស (Male)</option>
                      <option value="2">ស្រី (Female)</option>
                    </select>
                  </div>
                </div>

                <!-- Date of Birth -->
                <div class="col-md-6 col-12">
                  <div class="form-group-custom">
                    <label class="form-label-custom">
                      {{ $t("message.dob") }} <span class="text-danger">*</span>
                    </label>
                    <input
                      v-maska="'##/##/####'"
                      v-model="form.dob"
                      @input="updateForm"
                      class="form-control-custom"
                      :class="{ 'is-invalid': v$.form.dob.$error }"
                      placeholder="dd/mm/yyyy"
                    />
                  </div>
                </div>

                <!-- Nationality -->
                <div class="col-md-6 col-12">
                  <div class="form-group-custom">
                    <label class="form-label-custom">
                      {{ $t("message.nationality") }} <span class="text-danger">*</span>
                    </label>
                    <select
                      v-model="form.nationality"
                      class="form-select-custom"
                      :class="{ 'is-invalid': v$.form.nationality.$error }"
                      @change="setdailcode"
                    >
                      <option value="" disabled selected>Select nationality...</option>
                      <option
                        v-for="item in countryCode"
                        :key="item.id"
                        :value="item.id"
                      >
                        {{ item.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- ID Card / Passport -->
                <div class="col-md-6 col-12">
                  <div class="form-group-custom">
                    <label class="form-label-custom">
                      {{ $t("message.nationality_idcard_or_passport") }} <span class="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      class="form-control-custom"
                      :class="{ 'is-invalid': v$.form.national_id_card.$error || errors?.national_id_card }"
                      v-model="form.national_id_card"
                      @input="updateForm"
                    />
                    <div class="invalid-feedback-custom" v-if="errors?.national_id_card">
                      {{ errors?.national_id_card[0] }}
                    </div>
                  </div>
                </div>

                <!-- Phone -->
                <div class="col-md-6 col-12">
                  <div class="form-group-custom">
                    <label class="form-label-custom">
                      {{ $t("message.available_contact") }} <span class="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      v-maska="'##########'"
                      v-model="form.phone"
                      @input="updateForm"
                      class="form-control-custom"
                      :class="{ 'is-invalid': v$.form.phone.$error || errors?.phone }"
                    />
                    <div class="invalid-feedback-custom" v-if="errors?.phone">
                      {{ errors?.phone[0] }}
                    </div>
                  </div>
                </div>

                <!-- Email -->
                <div class="col-md-6 col-12">
                  <div class="form-group-custom">
                    <label class="form-label-custom">
                      {{ $t("message.email") }} <span class="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      v-model="form.email"
                      @input="updateForm"
                      class="form-control-custom"
                      placeholder="example@mail.com"
                      :class="{ 'is-invalid': v$.form.email.$error || errors?.email }"
                    />
                    <div class="invalid-feedback-custom" v-if="errors?.email">
                      {{ errors?.email[0] }}
                    </div>
                  </div>
                </div>

                <!-- Password -->
                <div class="col-md-6 col-12">
                  <div class="form-group-custom">
                    <label class="form-label-custom">
                      {{ $t("message.password") }} <span class="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      v-model="form.password"
                      @input="updateForm"
                      class="form-control-custom"
                      placeholder="••••••••"
                      :class="{ 'is-invalid': v$.form.password.$error }"
                    />
                  </div>
                </div>

                <!-- Confirm Password -->
                <div class="col-md-6 col-12">
                  <div class="form-group-custom">
                    <label class="form-label-custom">
                      {{ $t("message.confirm_password") }} <span class="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      v-model="form.password_confirmation"
                      @input="updateForm"
                      class="form-control-custom"
                      placeholder="••••••••"
                      :class="{ 'is-invalid': v$.form.password_confirmation.$error }"
                    />
                  </div>
                </div>
              </div>

              <!-- Submit Section -->
              <div class="submit-actions-section mt-5 text-center">
                <button
                  type="button"
                  @click="saveConfirm"
                  class="btn-premium-register-form animate-glow"
                >
                  <span>{{ $t("message.create_account") }}</span>
                  <i class="fas fa-user-plus"></i>
                </button>
              </div>
            </form>
          </div>

          <!-- Form 2: Verification Code Form -->
          <div v-if="showCodeInput" class="verification-section text-center py-4 animate-fade-in">
            <div class="verification-badge mb-4">
              <i class="fas fa-envelope-open-text"></i>
            </div>
            
            <h4 class="verification-title mb-2">Email Verification</h4>
            <p class="verification-desc text-muted mb-4">
              Enter the confirmation code we just sent to your email:<br />
              <strong class="text-emerald">{{ form.email }}</strong>
            </p>

            <form @submit.prevent class="verification-form">
              <div class="code-input-wrapper mb-4">
                <input
                  class="form-control-code"
                  v-model="form.verification_code"
                  type="text"
                  placeholder="Code"
                  maxLength="6"
                />
              </div>
              
              <div class="verification-buttons mb-4">
                <button
                  type="button"
                  class="btn-premium-verify"
                  @click="signInWithVerificationCode"
                >
                  Verify
                </button>
              </div>

              <p class="resend-question text-muted mb-3">Didn't receive the code?</p>
              
              <div class="navigation-actions d-flex justify-content-center gap-3">
                <button
                  type="button"
                  class="btn-nav-outline"
                  @click="goBack"
                >
                  <i class="fas fa-arrow-left"></i> {{ $t("message.back") }}
                </button>
                <button
                  type="button"
                  class="btn-nav-gold"
                  @click="sendCodeSMS"
                >
                  {{ $t("message.resend") }} <i class="fas fa-redo-alt"></i>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from "firebase";
import axios from "@/axios";
import useVuelidate from "@vuelidate/core";
import { required, requiredIf, email, sameAs } from "@vuelidate/validators";
import { mapGetters } from "vuex";
import { mapActions } from "vuex";

export default {
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      recaptcha: "",
      token: "",
      showNumberInput: true,
      showCodeInput: false,
      code: { one: '', two: '', three: '', foure: '', five: '', six: '' },
      errors: []
    };
  },
  validations() {
    return {
      form: {
        participant_type: { required, $autoDirty: true },
        company_name: {
          requiredIf: requiredIf(() => {
            return this.form.participant_type == 2;
          }),
          $autoDirty: true
        },
        firstname: { required, $autoDirty: true },
        lastname: { required, $autoDirty: true },
        firstname_en: { required, $autoDirty: true },
        lastname_en: { required, $autoDirty: true },
        gender: { required, $autoDirty: true },
        dob: { required, $autoDirty: true },
        nationality: { required, $autoDirty: true },
        national_id_card: { required, $autoDirty: true },
        phone: { required, $autoDirty: true },
        email: { required, email, $autoDirty: true },
        password: { required, $autoDirty: true },
        password_confirmation: {
          required,
          sameAsPassword: sameAs(this.form.password),
          $autoDirty: true
        }
      }
    };
  },
  computed: {
    ...mapGetters({
      form: "getSingUp",
      countryCode: "getCountryCode"
    })
  },
  methods: {
    ...mapActions(["act_loadCountryCode"]),
    updateForm() {
      this.$store.dispatch("act_changesingup", this.form);
    },
    setdailcode() {
      const dial_code = this.countryCode.filter(
        item => item.id == this.form.nationality
      )[0].dial_code;
      this.form.dial_code = dial_code;
      this.$store.dispatch("act_changesingup", this.form);
    },
    onCancelLoader() {
      console.log("User cancelled the loader.");
    },
    async sendCodeSMS() {
      let loader = this.showLoader();
      let response = await axios.post("request-opt-code", {
        email: this.form.email
      });
      if (response.data.status == 200) {
        this.showCodeInput = true;
        this.showNumberInput = false;
        this.form.verification_code = "";
        this.$store.dispatch("act_changesingup", this.form);
        loader.hide();
      } else {
        loader.hide();
        this.$toast.open({
          message: response.data.message,
          type: "error",
          duration: 5000,
          dismissible: true,
          position: "top-right"
        });
      }
    },
    goBack() {
      this.showNumberInput = true;
      this.showCodeInput = false;
      this.resetRe();
    },
    showLoader() {
      return this.$loading.show({
        container: this.$refs.formContainer,
        color: "#01B201",
        canCancel: true,
        onCancel: this.onCancelLoader
      });
    },
    saveCreateForm() {
      let loader = this.showLoader();
      const res = this.$store.dispatch("act_signup", {
        form: this.form,
        router: this.$router
      });

      res
        .then(rs => {
          loader.hide();
          console.log("after dispatch is success", rs);
          if (rs.status === 422) {
            console.log('it is :', rs.status);
            var m = rs.data.message;
            if (Object.prototype.hasOwnProperty.call(rs.data, 'errors')) {
              const message = Object.values(rs.data.errors).join(",");
              m = m + message;
            }
            this.$toast.open({
              message: m,
              type: "error",
              duration: 5000,
              dismissible: true,
              position: "top-right"
            });

            if (Object.prototype.hasOwnProperty.call(rs.data, 'field')) {
              if (rs.data.field == 'phone') {
                this.form.phone = '';
              } else if (rs.data.field == 'national_id_card') {
                this.form.national_id_card = '';
              }
            }

            this.goBack();

          } else if (rs.status === 200) {
            this.$router.push({ name: "register" });
          }
        })
        .catch(err => {
          loader.hide();
          console.log("after dispatch is error", err);
        });
    },
    async saveConfirm() {
      this.v$.$validate();
      if (!this.v$.$error) {
        try {
          let response = await axios.post("check-data", this.form);
          if (!response.data.success) {
            if (Object.prototype.hasOwnProperty.call(response.data.errors, 'national_id_card')) {
              this.form.national_id_card = '';
            }
            if (Object.prototype.hasOwnProperty.call(response.data.errors, 'phone')) {
              this.form.phone = '';
            }
            if (Object.prototype.hasOwnProperty.call(response.data.errors, 'email')) {
              this.form.email = '';
            }
            this.$toast.open({
              message: "data already exist! : " + Object.keys(response.data.errors).join(', '),
              type: "error",
              duration: 5000,
              dismissible: true,
              position: "top-right"
            });
          } else {
            this.sendCodeSMS();
          }
        } catch (error) {
          this.errors = { ...error.response.data.errors };
          console.log(error.response.data.errors);
          console.log('data errors:', this.errors);
          let a = {};
          for (const [key] of Object.entries(error.response.data.errors)) {
            a[key] = '';
          }
        
          Object.assign(this.form, a);
          this.$toast.open({
            message: "data already exist! : " + Object.keys(error.response.data.errors).join(', '),
            type: "error",
            duration: 5000,
            dismissible: true,
            position: "top-right"
          });
        }
      } else {
        this.$toast.open({
          message: "Form is invalid",
          type: "error",
          duration: 5000,
          dismissible: true,
          position: "top-right"
        });
      }
    },
    async signInWithVerificationCode() {
      let response = await axios.post("verify-opt-code", {
        email: this.form.email,
        otp: this.form.verification_code
      });
      if (response.data.status == 200) {
        this.saveCreateForm();
      } else {
        this.$toast.open({
          message: response.data.message,
          type: "error",
          duration: 5000,
          dismissible: true,
          position: "top-right"
        });
      }
    },
    changeFocus(id) {
      const v = id + 1;
      this.$refs["input_" + v].focus();
    },
    initailAuth() {
      firebase.auth().languageCode = "km";
      window.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
        "get-sign-in-code",
        {
          size: "invisible",
          callback: response => {
            console.log("response recapcha", response);
            console.log("it is run");
          }
        }
      );

      window.recaptchaVerifier.render().then(widgetId => {
        window.recaptchaWidgetId = widgetId;
      });
    },
    async getCamDigikey() {
      let loader = this.showLoader();
      try {
        let response = await axios.post("get-camdigikey");
        if (response.data.status) {
          loader.hide();
          window.location.href = response.data.response.loginUrl;
        } else {
          loader.hide();
          this.$toast.open({
            message: "Login fail " + response.message,
            type: "error",
            duration: 5000,
            dismissible: true,
            position: "top-right"
          });
        }
      } catch (error) {
        loader.hide();
      }
    }
  },
  created() {},
  mounted() {
    this.initailAuth();
    this.act_loadCountryCode();
  }
};
</script>

<style scoped>
.signup-page-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #0e8937 0%, #05170d 100%);
  font-family: 'Battambang', 'Inter', sans-serif;
  overflow-x: hidden;
}

/* Card Outer Container */
.signup-card {
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
}

/* Card Header Banner */
.signup-header {
  position: relative;
  background: url("~@/assets/images/bg.jpg") no-repeat top center;
  background-size: cover;
  padding: 40px 30px;
  color: #ffffff;
  border-bottom: 4px solid #ffc107;
}

.header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(9, 26, 16, 0.65) 0%,
    rgba(5, 12, 8, 0.78) 100%
  );
  z-index: 1;
}

.header-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.brand-logo-link {
  display: inline-block;
  margin-bottom: 16px;
}

.brand-logo {
  width: 80px;
  height: auto;
  transition: transform 0.4s ease;
}

.brand-logo:hover {
  transform: rotate(-3deg) scale(1.08);
}

.system-title {
  font-size: 1.15rem;
  line-height: 1.6;
  margin-bottom: 8px;
  max-width: 700px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
}

.form-action-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.75);
  letter-spacing: 0.5px;
  margin-bottom: 16px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}

/* CamDigiKey SSO button */
.btn-camdigikey-sso {
  background: #ffffff;
  color: #0056b3;
  border: 1.5px solid #0056b3;
  padding: 10px 24px;
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.25s;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn-camdigikey-sso:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 86, 179, 0.25);
}

.btn-cam-img {
  width: 18px;
  height: auto;
  border-radius: 3px;
}

/* Body Section */
.signup-body {
  padding: 50px 40px;
  background-color: #ffffff;
}

.section-title-custom {
  font-size: 1.4rem;
  color: #2c3e35;
  position: relative;
  display: inline-block;
  width: 100%;
}

.section-title-custom::after {
  content: '';
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 3px;
  background: #0e8937;
  border-radius: 2px;
}

/* Form Groups */
.form-group-custom {
  margin-bottom: 0;
}

.form-label-custom {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #4a5c50;
  margin-bottom: 8px;
}

.form-control-custom {
  width: 100%;
  padding: 12px 16px;
  background: #f4f5f7;
  border: 1.5px solid #edf0f2;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #2c3e35;
  transition: all 0.25s ease;
}

.form-control-custom:focus {
  background: #ffffff;
  border-color: #0e8937;
  outline: none;
  box-shadow: 0 0 0 3px rgba(14, 137, 55, 0.1);
}

.form-control-custom.is-invalid {
  border-color: #dc3545;
  background-color: #fdf3f4;
}

.form-select-custom {
  width: 100%;
  padding: 12px 16px;
  background: #f4f5f7;
  border: 1.5px solid #edf0f2;
  border-radius: 10px;
  font-size: 0.95rem;
  color: #2c3e35;
  transition: all 0.25s ease;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%234a5c50' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 12px 12px;
}

.form-select-custom:focus {
  background-color: #ffffff;
  border-color: #0e8937;
  outline: none;
  box-shadow: 0 0 0 3px rgba(14, 137, 55, 0.1);
}

.form-select-custom.is-invalid {
  border-color: #dc3545;
  background-color: #fdf3f4;
}

.invalid-feedback-custom {
  display: block;
  font-size: 0.78rem;
  color: #dc3545;
  margin-top: 4px;
  padding-left: 4px;
}

/* Form Actions Button */
.btn-premium-register-form {
  position: relative;
  background: linear-gradient(135deg, #0e8937 0%, #0a5c2c 100%);
  color: #ffffff;
  border: none;
  padding: 14px 44px;
  font-size: 1.05rem;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(14, 137, 55, 0.2);
  display: inline-flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-premium-register-form:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(14, 137, 55, 0.35);
  background: linear-gradient(135deg, #ffc107 0%, #e0a800 100%);
  color: #050b07;
}

/* Verification Code UI */
.verification-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: rgba(14, 137, 55, 0.08);
  border-radius: 50%;
  color: #0e8937;
  font-size: 28px;
  border: 1px solid rgba(14, 137, 55, 0.15);
}

.verification-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #2c3e35;
}

.text-emerald {
  color: #0e8937;
  font-weight: 600;
}

.code-input-wrapper {
  max-width: 250px;
  margin: 0 auto;
}

.form-control-code {
  width: 100%;
  padding: 14px;
  font-size: 1.4rem;
  letter-spacing: 4px;
  font-weight: 700;
  text-align: center;
  background: #f4f5f7;
  border: 1.5px solid #edf0f2;
  border-radius: 10px;
  color: #2c3e35;
  transition: all 0.25s ease;
}

.form-control-code:focus {
  background: #ffffff;
  border-color: #0e8937;
  outline: none;
  box-shadow: 0 0 0 3px rgba(14, 137, 55, 0.1);
}

.btn-premium-verify {
  background: linear-gradient(135deg, #0e8937 0%, #0a5c2c 100%);
  color: #ffffff;
  border: none;
  padding: 12px 48px;
  font-weight: 600;
  font-size: 1rem;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(14, 137, 55, 0.25);
  transition: all 0.25s;
}

.btn-premium-verify:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(14, 137, 55, 0.35);
  background: linear-gradient(135deg, #ffc107 0%, #e0a800 100%);
  color: #050b07;
}

.btn-nav-outline {
  background: transparent;
  color: #4a5c50;
  border: 1.5px solid #edf0f2;
  padding: 10px 24px;
  font-weight: 600;
  font-size: 0.88rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-nav-outline:hover {
  background: #f4f5f7;
  color: #2c3e35;
}

.btn-nav-gold {
  background: rgba(255, 193, 7, 0.1);
  color: #d39e00;
  border: 1.5px solid rgba(255, 193, 7, 0.25);
  padding: 10px 24px;
  font-weight: 600;
  font-size: 0.88rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-nav-gold:hover {
  background: rgba(255, 193, 7, 0.2);
  color: #b28500;
}

/* Animations */
.animate-fade-in {
  animation: fadeIn 0.8s ease-out forwards;
}

.animate-bounce-subtle {
  animation: bounceSubtle 3s infinite ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes bounceSubtle {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

/* Responsive Rules */
@media (max-width: 768px) {
  .signup-card {
    border-radius: 16px;
  }
  
  .signup-header {
    padding: 30px 20px;
  }
  
  .signup-body {
    padding: 30px 20px;
  }
}
</style>
